import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { RecordFactory } from '../../models/RecordFactory'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { useOnboardingContext } from '../../providers/OnboardingProvider'
import { LOCAL_STORAGE_WATCHED_ONBOARDING_VIDEO } from '../../utils/constants'
import { Spinner } from '../Spinner'
import { FullScreenVideoModal } from '../app/FullScreenVideoModal'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import { WorkflowPage } from '../workflows/WorkflowPage'

function PlayIcon() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="-2 0 24 24"
            fill="none"
        >
            <rect width="24" height="24" fill="currentColor" />
            <path
                d="M5 6.66333C5 5.081 6.75049 4.12532 8.08152 4.98097L16.383 10.3176C17.6076 11.1049 17.6076 12.8951 16.383 13.6824L8.08152 19.019C6.75049 19.8747 5 18.919 5 17.3367V6.66333Z"
                fill="#314A68"
                stroke="#314A68"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}

function PlayVideoButton({ onClick }: { onClick?: () => void }) {
    return (
        <div
            className={clsx(
                'absolute top-1/2 left-1/2 flex size-14 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white text-white',
                'group-hover:bg-linear-to-b group-hover:from-white/4 group-hover:to-black/4 group-hover:text-white/4',
                'transition-opacity duration-200'
            )}
            onClick={onClick}
        >
            <PlayIcon />
        </div>
    )
}

export function SetupDashboardVerifyWelcomePage() {
    const { data, isLoading } = useQuery({
        queryKey: ['dashboard', 'setup_launch'],
        queryFn: async () => {
            const result = await RecordFactory.getInstance().fetchDashboard(
                'setup_launch',
                undefined,
                undefined,
                0,
                'all'
            )
            if (!result.ok) {
                return null
            }

            return { dashboard: result.value }
        },
    })

    const navigate = useNavigate()

    const { advanceOnboardingState } = useOnboardingContext()

    const [showVideoModal, setShowVideoModal] = useState(false)
    const [didWatchVideo, setDidWatchVideo] = useState(false)
    const [skipVisible, setSkipVisible] = useState(false)

    const { findLastHistoryEntry } = useHistoryTrackerContext()
    const lastHistoryEntry = findLastHistoryEntry()

    const onNextHandler = async () => {
        await advanceOnboardingState()
    }

    useEffect(() => {
        const watchedVideo = localStorage.getItem(LOCAL_STORAGE_WATCHED_ONBOARDING_VIDEO)
        if (watchedVideo) {
            setDidWatchVideo(true)
        }
    }, [])

    const videoFinished = () => {
        localStorage.setItem(LOCAL_STORAGE_WATCHED_ONBOARDING_VIDEO, '1')
        setDidWatchVideo(true)
        setShowVideoModal(false)
        setSkipVisible(false)
    }

    const onClose = () => {
        setShowVideoModal(false)
        setSkipVisible(true)
    }

    if (isLoading || !data) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Spinner />
            </div>
        )
    }

    return (
        <>
            <WorkflowPage
                title="Your payments"
                onClose={() => navigate('/')}
                onBack={lastHistoryEntry?.goBackTo}
                backButtonTitle={lastHistoryEntry?.title}
                footerContent={
                    <div className="flex justify-end">
                        <Button
                            intent="primary"
                            type="submit"
                            disabled={!didWatchVideo && !skipVisible}
                            onClick={onNextHandler}
                        >
                            Next
                        </Button>
                    </div>
                }
            >
                <Paragraph size="md" className="mt-4 max-w-[580px] text-center text-gray-600">
                    PocketSuite is easy for your clients to pay you! Watch a quick overview video to
                    learn more
                </Paragraph>
                <WorkflowPage.Card className="mt-12">
                    <div className="group relative">
                        <img
                            alt="onboarding laptop &amp; phone"
                            src="/icons/golive-laptop-phone.png"
                            className="cursor-pointer rounded-lg group-hover:opacity-96"
                            onClick={() => setShowVideoModal(true)}
                        />
                        <PlayVideoButton onClick={() => setShowVideoModal(true)} />
                    </div>
                    <Button intent="primary" onClick={() => setShowVideoModal(true)}>
                        Watch video
                    </Button>
                </WorkflowPage.Card>
            </WorkflowPage>
            <FullScreenVideoModal
                isOpen={showVideoModal}
                onClose={onClose}
                hlsUrl={data?.dashboard?.metaData.verify_video_uri}
                onEnded={videoFinished}
            />
        </>
    )
}
